import { useCallback, useRef } from 'react';

/** Long enough not to fire on a tap, short enough not to feel stuck. */
const HOLD_MS = 450;
/** Past this the finger is scrolling, not holding. */
const MOVE_TOLERANCE_PX = 10;

/**
 * Press and hold, for the actions a touch screen has no hover to reveal.
 *
 * Touch and pen only. A mouse keeps the hover gutter it already has, and
 * firing this on a slow left-click as well would mean a menu opening on top of
 * buttons that are already visible.
 *
 * The two things that make or break it:
 *
 *   - **It has to lose to scrolling.** A thread is scrolled by dragging over
 *     the very messages this is attached to, so any movement past a few pixels
 *     cancels the hold. Without that, a slow flick opens a menu instead of
 *     scrolling and the conversation feels broken.
 *   - **The browser's own long-press has to be suppressed**, or iOS raises the
 *     text-selection callout over the menu. That is done in CSS on the element
 *     (`-webkit-touch-callout: none`), plus the contextmenu handler below for
 *     Android, which otherwise pops its own menu at the same moment.
 */
export const useLongPress = (onLongPress?: () => void) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  /** Set when the hold fired, so the click it turns into can be swallowed. */
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    originRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (!onLongPress) return;
      if (event.pointerType === 'mouse') return;

      firedRef.current = false;
      originRef.current = { x: event.clientX, y: event.clientY };
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        cancel();
        /* The same tick the swipe gives at its point of no return - the menu
           opening under a still finger otherwise has no acknowledgement at
           all. Where supported: Android has the API, iOS Safari has nothing
           to call. */
        navigator.vibrate?.(15);
        onLongPress();
      }, HOLD_MS);
    },
    [onLongPress, cancel]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const origin = originRef.current;
      if (!origin) return;
      const moved =
        Math.abs(event.clientX - origin.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - origin.y) > MOVE_TOLERANCE_PX;
      if (moved) cancel();
    },
    [cancel]
  );

  /**
   * Swallows the click the browser synthesises after the hold, so opening the
   * menu on a picture does not also open the lightbox behind it.
   */
  const onClickCapture = useCallback((event: React.MouseEvent) => {
    if (!firedRef.current) return;
    firedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onClickCapture,
    onContextMenu: (event: React.MouseEvent) => {
      if (onLongPress) event.preventDefault();
    },
  };
};
